import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { actions } from '@/lib/actions';
import { api, errorMessage } from '@/lib/api';
import { dayLabel, formatAgo, formatDate, formatMoney, formatNumber, formatTime, plural } from '@/lib/format';
import { useProfileTerms, useRates, useStages, useTeam } from '@/lib/hooks';
import { openRecord } from '@/lib/overlays';
import { Slot } from '@/lib/slots';
import { toast, toastError } from '@/lib/toast';
import { Avatar, CompanyMono } from '@/ui/Avatar';
import { Callout, Chip, Dot, RoundCheck, Skeleton } from '@/ui/bits';
import { Icon } from '@/ui/Icon';
import { CapabilityChips } from '@/ui/ProfileTerms';
import { gapsYouFill, sameTerm, termList } from '@shared/profiles';
import { firstName, type CompanySheet, type MeetingItem, type PersonSheet, type RecordSheet, type SheetDeal } from '@shared/record';
import type { FollowupItem } from '@shared/types';
import { keys, useMeetings, useRecordFollowups } from './queries';
import { meetingTimes, startsIn } from './Timeline';

// The boxes down the right of the record sheet: deals, the company or its people, follow-ups and meetings.

function Retry({ title, error, retry }: { title: string; error: unknown; retry: () => void }) {
  return (
    <Callout
      tone="danger"
      title={title}
      actions={
        <button type="button" className="btn btn-sm btn-tile" onClick={retry}>
          <Icon name="refresh" />
          Try again
        </button>
      }
    >
      {errorMessage(error)}
    </Callout>
  );
}

/* ---------- deals ---------- */

function DealRow({ deal }: { deal: SheetDeal }) {
  const { stages } = useStages();
  const open = stages.filter((s) => s.kind === 'open');
  const at = deal.stage ? open.findIndex((s) => s.id === deal.stage!.id) : -1;
  return (
    <div className="rel-deal">
      <span className="r1">
        <Dot color={deal.stage?.color} />
        <b>{deal.name}</b>
        <span className="v">{formatMoney(deal.value, deal.currency)}</span>
      </span>
      <span className="r2">
        {deal.stage?.name ?? 'No stage'}
        {deal.owner ? (
          <>
            {' · '}
            <Avatar name={deal.owner.name} hue={deal.owner.hue} size="xs" decorative />
            {firstName(deal.owner.name)}
          </>
        ) : null}
      </span>
      {open.length && at >= 0 ? (
        <span className="stagebar" aria-hidden="true">
          {open.map((s, i) => (
            <i key={s.id} className={i <= at ? 'on' : undefined} />
          ))}
        </span>
      ) : null}
      {deal.nextStep ? (
        <span className="r2">
          <Icon name="flag" className="i-sm" />
          {deal.nextStep}
        </span>
      ) : null}
    </div>
  );
}

/** Shown when the deals area is not part of this build: the open deals, read-only. */
function DealsFallback({ sheet }: { sheet: RecordSheet }) {
  const deals = sheet.openDeals;
  const { toMain, mainCurrency } = useRates();
  const sum = deals.reduce((s, d) => s + toMain(Number(d.value) || 0, d.currency), 0);
  return (
    <section className="tile" aria-labelledby={`deals-${sheet.id}`}>
      <div className="tile-h">
        <h3 id={`deals-${sheet.id}`}>Deals</h3>
        <span className="cnt">{deals.length > 1 ? `${deals.length} · ${formatMoney(sum, mainCurrency)}` : deals.length}</span>
      </div>
      {deals.length ? (
        deals.map((d) => <DealRow key={d.id} deal={d} />)
      ) : (
        <p className="muted box-empty">No open deals yet. Start one when the conversation turns to work.</p>
      )}
    </section>
  );
}

export function DealsBox({ sheet, focus }: { sheet: RecordSheet; focus?: string }) {
  return <Slot name="record.deals" type={sheet.type} id={sheet.id} recordName={sheet.name} focus={focus} fallback={<DealsFallback sheet={sheet} />} />;
}

/* ---------- company and people ---------- */

export function CompanyBox({ sheet }: { sheet: PersonSheet }) {
  const co = sheet.company;
  const terms = useProfileTerms(!!co?.lacks.length);
  const offerings = terms.data?.offerings ?? [];
  const fits = co ? gapsYouFill(co.lacks, offerings) : [];
  const industry = co?.industry && !(co.market && sameTerm(co.market, co.industry)) ? co.industry : null;
  return (
    <section className="tile" aria-labelledby={`co-${sheet.id}`}>
      <div className="tile-h">
        <h3 id={`co-${sheet.id}`}>Company</h3>
        {fits.length ? (
          <span className="chip chip-accent prof-fit" data-tip={`You offer ${termList(fits)}`}>
            <Icon name="target" />
            {fits.length === 1 ? 'You fill 1 gap' : `You fill ${fits.length} gaps`}
          </span>
        ) : null}
      </div>
      {co ? (
        <>
          <button type="button" className="mini-row" onClick={() => openRecord('company', co.id)}>
            <CompanyMono name={co.name} hue={co.hue} logo={co.logo} size="sm" />
            <span className="grow">
              <b>{co.name}</b>
              <span>{[co.market, industry, co.staff !== null ? `${formatNumber(co.staff)} staff` : null, co.location].filter(Boolean).join(' · ') || co.domain}</span>
            </span>
            <Icon name="chev-right" className="i-sm" />
          </button>
          {co.lacks.length ? (
            <div className="co-gaps" role="group" aria-label={`Gaps at ${co.name}`}>
              <span className="co-gaps-k">Gaps</span>
              <span className="tags">
                <CapabilityChips names={co.lacks} side="lacks" offerings={offerings} limit={4} />
              </span>
            </div>
          ) : null}
          {sheet.mates.map((m) => (
            <button type="button" className="mini-row" key={m.id} onClick={() => openRecord('person', m.id)}>
              <Avatar name={m.name} hue={m.hue} size="sm" decorative />
              <span className="grow">
                <b>{m.name}</b>
                {m.title ? <span>{m.title}</span> : null}
              </span>
            </button>
          ))}
        </>
      ) : (
        <p className="muted box-empty">{firstName(sheet.name)} is on their own for now. Set a company in Details to see their colleagues here.</p>
      )}
    </section>
  );
}

const DECIDER = /decision/i;

export function PeopleBox({ sheet, readOnly }: { sheet: CompanySheet; readOnly: boolean }) {
  return (
    <section className="tile" aria-labelledby={`ppl-${sheet.id}`}>
      <div className="tile-h">
        <h3 id={`ppl-${sheet.id}`}>People</h3>
        <span className="cnt">{sheet.people.length}</span>
        {readOnly ? null : (
          <button type="button" className="tile-more" onClick={() => actions.newRecord('person', { companyId: sheet.id })} aria-label={`Add a person at ${sheet.name}`}>
            <Icon name="plus" />
            Add
          </button>
        )}
      </div>
      {sheet.people.length ? (
        sheet.people.map((m) => {
          const decider = m.tags.find((t) => DECIDER.test(t));
          return (
            <button type="button" className="mini-row" key={m.id} onClick={() => openRecord('person', m.id)}>
              <Avatar name={m.name} hue={m.hue} size="sm" decorative />
              <span className="grow">
                <b>{m.name}</b>
                <span>{[m.title, m.lastContactedAt ? formatAgo(m.lastContactedAt) : null].filter(Boolean).join(' · ')}</span>
              </span>
              {decider ? <Chip tone="accent">{decider}</Chip> : null}
            </button>
          );
        })
      ) : (
        <p className="muted box-empty">No people yet. Add the first contact here.</p>
      )}
      {readOnly ? null : <Slot name="record.people" companyId={sheet.id} companyName={sheet.name} />}
    </section>
  );
}

/* ---------- follow-ups ---------- */

function dueText(f: FollowupItem): string {
  const day = dayLabel(f.dueAt);
  return f.hasTime ? `${day} ${formatTime(f.dueAt)}` : day;
}

function overdue(f: FollowupItem): boolean {
  const due = new Date(f.dueAt);
  if (f.hasTime) return due.getTime() < Date.now();
  const end = new Date(due);
  end.setHours(23, 59, 59, 999);
  return end.getTime() < Date.now();
}

export function FollowupsBox({ sheet, readOnly }: { sheet: RecordSheet; readOnly: boolean }) {
  const qc = useQueryClient();
  const q = useRecordFollowups(sheet.type, sheet.id);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const list = q.data ?? [];

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: keys.followups(sheet.type, sheet.id) });
    void qc.invalidateQueries({ queryKey: keys.timeline(sheet.type, sheet.id) });
  };
  const tick = async (f: FollowupItem) => {
    setTicked((s) => new Set(s).add(f.id));
    try {
      await api.patch(`/followups/${f.id}`, { done: true });
      toast(`Done: ${f.title}`, {
        icon: 'check-circle',
        undo: () => {
          api
            .patch(`/followups/${f.id}`, { done: false })
            .then(refresh)
            .catch((err) => toastError(err));
        },
      });
      refresh();
    } catch (err) {
      toastError(err);
    } finally {
      setTicked((s) => {
        const next = new Set(s);
        next.delete(f.id);
        return next;
      });
    }
  };
  const add = () => actions.addFollowup(sheet.type === 'person' ? { personId: sheet.id } : { companyId: sheet.id });

  return (
    <section className="tile" aria-labelledby={`fu-${sheet.id}`}>
      <div className="tile-h">
        <h3 id={`fu-${sheet.id}`}>Follow-ups</h3>
        {q.data ? <span className="cnt">{list.length}</span> : null}
        {readOnly ? null : (
          <button type="button" className="tile-more" onClick={add} aria-label={`Add a follow-up for ${sheet.name}`}>
            <Icon name="plus" />
            Add
          </button>
        )}
      </div>
      {q.isPending ? (
        <Skeleton rows={2} />
      ) : q.isError ? (
        <Retry title="The follow-ups did not load" error={q.error} retry={() => void q.refetch()} />
      ) : list.length ? (
        <div aria-live="polite">
          {list.map((f) => {
            const late = overdue(f);
            const about = [f.deal?.name ?? (sheet.type === 'company' && f.person ? f.person.name : null), f.owner ? firstName(f.owner.name) : null]
              .filter(Boolean)
              .join(' · ');
            return (
              <div className={`mini-row${ticked.has(f.id) ? ' is-done' : ''}`} key={f.id}>
                <RoundCheck checked={ticked.has(f.id)} onChange={() => void tick(f)} label={`Complete: ${f.title}`} />
                <span className="grow">
                  <b>{f.title}</b>
                  {about ? <span>{about}</span> : null}
                </span>
                <span className={`when${late ? ' late' : ''}`}>{late ? `${dueText(f)} · overdue` : dueText(f)}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="muted box-empty">Nothing scheduled. Add a follow-up from the composer.</p>
      )}
    </section>
  );
}

/* ---------- meetings ---------- */

function DateTile({ at, past }: { at: string; past?: boolean }) {
  const [day, month] = formatDate(at).split(' ');
  return (
    <span className={`date-tile${past ? ' past' : ''}`} aria-hidden="true">
      <small>{month}</small>
      <b>{day}</b>
    </span>
  );
}

function MeetingRow({ m, past }: { m: MeetingItem; past: boolean }) {
  const { members } = useTeam();
  const ours = new Set(members.flatMap((t) => [t.email, ...t.emails.map((e) => e.email)]).filter(Boolean).map((e) => e!.toLowerCase()));
  const guests = m.attendees.filter((a) => !ours.has(a.email.toLowerCase())).map((a) => a.name ?? a.email);
  const sub = [
    past && !m.allDay ? formatDate(m.startsAt) : meetingTimes(m),
    guests.length ? `with ${guests.length > 2 ? `${guests.slice(0, 2).join(', ')} and ${guests.length - 2} more` : guests.join(' and ')}` : null,
    m.meetUrl ? 'Meet' : m.location,
  ]
    .filter(Boolean)
    .join(' · ');
  const inner = (
    <>
      <DateTile at={m.startsAt} past={past} />
      <span className="grow">
        <b>{m.title}</b>
        <span>{sub}</span>
      </span>
      <span className={`when${past ? ' past' : ''}`}>{past ? 'Past' : startsIn(m)}</span>
    </>
  );
  const link = !past ? (m.meetUrl ?? m.htmlLink) : m.htmlLink;
  return link ? (
    <a className="mini-row" href={link} target="_blank" rel="noreferrer" aria-label={`${m.title}, ${sub}${past ? '' : `, ${m.meetUrl ? 'join' : 'open in Google Calendar'}`}`}>
      {inner}
    </a>
  ) : (
    <div className="mini-row">{inner}</div>
  );
}

export function MeetingsBox({ sheet }: { sheet: RecordSheet }) {
  const q = useMeetings(sheet.type, sheet.id);
  if (q.isPending) return null;
  if (q.isError)
    return (
      <section className="tile" aria-label="Meetings">
        <Retry title="The meetings did not load" error={q.error} retry={() => void q.refetch()} />
      </section>
    );
  const upcoming = q.data.upcoming.slice(0, 3);
  const past = q.data.past.slice(0, Math.max(1, 4 - upcoming.length));
  if (!upcoming.length && !past.length) return null;
  return (
    <section className="tile" aria-labelledby={`mt-${sheet.id}`}>
      <div className="tile-h">
        <h3 id={`mt-${sheet.id}`}>Meetings</h3>
        <span className="hint">
          <Icon name="calendar" />
          Google Calendar
        </span>
      </div>
      {upcoming.map((m) => (
        <MeetingRow key={m.id} m={m} past={false} />
      ))}
      {past.map((m) => (
        <MeetingRow key={m.id} m={m} past />
      ))}
      {q.data.upcoming.length + q.data.past.length > upcoming.length + past.length ? (
        <p className="muted box-more">{plural(q.data.upcoming.length + q.data.past.length, 'meeting')} in all, each on the timeline.</p>
      ) : null}
    </section>
  );
}
